import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { assignAsset, listAssets, listAssignees, listAssignmentHistory } from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { formatDateShort } from "../lib/format";
import { PageBar } from "../components/Layout";
import { Modal } from "../components/Modal";
import { confirmDialog } from "../components/ConfirmDialog";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { SortTh, useTableSort, type SortAccessor } from "../components/SortableTable";
import { Pagination, usePagination } from "../components/Pagination";
import type { AssetAssignmentPeriodRow, AssetRow } from "../lib/types";

const HELD_SORT: Record<string, SortAccessor<AssetRow>> = {
  holder: (a) => a.holder_name,
  asset: (a) => a.name,
  tag: (a) => a.asset_tag,
  type: (a) => a.type_name,
  qty: (a) => a.quantity ?? 1,
  location: (a) => a.location_name,
  since: (a) => a.updated_at,
};

const HISTORY_SORT: Record<string, SortAccessor<AssetAssignmentPeriodRow>> = {
  holder: (h) => h.user_name,
  asset: (h) => h.asset_name,
  from: (h) => h.start_at,
  to: (h) => h.end_at,
  by: (h) => h.by_user_name,
  note: (h) => h.note,
};

/** Who currently has what — every asset with a holder. Custody is managed right
 *  here: check out an in-stock asset, reassign it to someone else, or check it
 *  back in. The full per-asset custody trail still lives on the asset detail. */
export function AssignmentsPage() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApi((signal) => listAssets(undefined, signal), []);
  // null = closed; { asset?: row } = open (asset preset → reassign; absent → check out).
  const [assigning, setAssigning] = useState<{ asset?: AssetRow } | null>(null);
  const checkInMut = useMutation((id: string) => assignAsset(id, null, { note: "Checked in" }));
  // Bumped on every custody change so the history card below re-fetches.
  const [historyKey, setHistoryKey] = useState(0);
  const refreshAll = () => {
    reload();
    setHistoryKey((k) => k + 1);
  };

  const [tab, setTab] = useState<"current" | "history">("current");
  const [heldQ, setHeldQ] = useState("");

  const assets = data?.assets ?? [];
  const held = useMemo(
    () =>
      assets
        .filter((a) => a.holder_id)
        .sort(
          (a, b) =>
            (a.holder_name ?? "").localeCompare(b.holder_name ?? "") || a.name.localeCompare(b.name),
        ),
    [assets],
  );
  // Filter the current-assignments list by holder / asset / tag / type / location.
  const filteredHeld = useMemo(() => {
    const q = heldQ.trim().toLowerCase();
    if (!q) return held;
    return held.filter((a) =>
      [a.holder_name, a.name, a.asset_tag, a.type_name, a.location_name].some((s) =>
        (s ?? "").toLowerCase().includes(q),
      ),
    );
  }, [held, heldQ]);
  const { rows: sortedHeld, sort: heldSort, toggle: heldToggle } = useTableSort(filteredHeld, HELD_SORT);
  const heldPg = usePagination(sortedHeld);
  // Jump back to page 1 whenever the filter changes (clamp alone keeps the last
  // valid page, but a new search should start at the top).
  useEffect(() => heldPg.setPage(0), [heldQ]); // eslint-disable-line react-hooks/exhaustive-deps
  // Available to hand out = in stock with no current holder.
  const available = useMemo(
    () => assets.filter((a) => a.status === "in_stock" && !a.holder_id).sort((a, b) => a.name.localeCompare(b.name)),
    [assets],
  );

  async function checkIn(a: AssetRow) {
    const ok = await confirmDialog({ message: `Check in "${a.name}" from ${a.holder_name}?` });
    if (!ok) return;
    const res = await checkInMut.run(a.id);
    if (res) refreshAll();
  }

  return (
    <>
      <PageBar
        crumbs="Assignments"
        actions={
          <button className="btn primary" onClick={() => setAssigning({})} disabled={loading}>
            Check out asset
          </button>
        }
      />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Assignments</h1>
            <div className="sub">Every asset currently in someone's possession.</div>
          </div>
        </div>

        <div className="view-switch" role="tablist" aria-label="Assignments view" style={{ marginBottom: 16 }}>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "current"}
            className={`view-switch-btn${tab === "current" ? " active" : ""}`}
            onClick={() => setTab("current")}
          >
            Currently assigned
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "history"}
            className={`view-switch-btn${tab === "history" ? " active" : ""}`}
            onClick={() => setTab("history")}
          >
            History
          </button>
        </div>

        {tab === "current" ? (
          <>
            <div className="table-filter">
              <input
                type="search"
                value={heldQ}
                onChange={(e) => setHeldQ(e.target.value)}
                placeholder="Filter by holder, asset, tag, type, or location…"
                aria-label="Filter current assignments"
              />
            </div>
            <div className="card">
            <div className="card-head">
              <h3>Currently assigned</h3>
              <span className="faint">{filteredHeld.length}</span>
            </div>
            <div className="card-body tight">
              {loading ? (
                <Loading label="Loading assignments…" />
              ) : error ? (
                <ErrorState error={error} onRetry={reload} />
              ) : held.length === 0 ? (
                <div style={{ padding: 18 }}>
                  <EmptyState>Nothing is checked out right now. Use “Check out asset” to assign one.</EmptyState>
                </div>
              ) : filteredHeld.length === 0 ? (
                <div style={{ padding: 18 }}>
                  <EmptyState>No assignments match “{heldQ}”.</EmptyState>
                </div>
              ) : (
                <>
                  <table className="data">
                    <thead>
                      <tr>
                        <SortTh field="holder" sort={heldSort} onSort={heldToggle}>Holder</SortTh>
                        <SortTh field="asset" sort={heldSort} onSort={heldToggle}>Asset</SortTh>
                        <SortTh field="tag" sort={heldSort} onSort={heldToggle}>Tag</SortTh>
                        <SortTh field="type" sort={heldSort} onSort={heldToggle}>Type</SortTh>
                        <SortTh field="qty" sort={heldSort} onSort={heldToggle}>Qty</SortTh>
                        <SortTh field="location" sort={heldSort} onSort={heldToggle}>Location</SortTh>
                        <SortTh field="since" sort={heldSort} onSort={heldToggle}>Since</SortTh>
                        <th aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {heldPg.pageRows.map((a) => (
                        <tr key={a.id}>
                          <td>
                            <strong>{a.holder_name}</strong>
                          </td>
                          <td>
                            <button className="task-title-link" onClick={() => navigate(`/assets/${a.id}`)}>
                              {a.name}
                            </button>
                          </td>
                          <td className="muted">{a.asset_tag || "—"}</td>
                          <td className="muted">{a.type_name || "—"}</td>
                          <td className="muted">{a.quantity ?? 1}</td>
                          <td className="muted">{a.location_name || "—"}</td>
                          <td className="muted">{formatDateShort(a.updated_at)}</td>
                          <td className="row-actions">
                            <button className="btn small" onClick={() => setAssigning({ asset: a })} disabled={checkInMut.pending}>
                              Reassign
                            </button>
                            <button className="btn small danger" onClick={() => checkIn(a)} disabled={checkInMut.pending}>
                              Check in
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Pagination pg={heldPg} />
                </>
              )}
            </div>
            </div>
          </>
        ) : (
          <AssignmentHistory key={historyKey} />
        )}
      </div>

      {assigning ? (
        <AssignModal
          asset={assigning.asset}
          available={available}
          onClose={() => setAssigning(null)}
          onDone={() => {
            setAssigning(null);
            refreshAll();
          }}
        />
      ) : null}
    </>
  );
}

/** Check out / reassign an asset to a holder. With a preset `asset` it's a
 *  reassignment (asset fixed, pick the new holder); without, it's a fresh
 *  check-out (pick an available asset + holder). */
function AssignModal({
  asset,
  available,
  onClose,
  onDone,
}: {
  asset: AssetRow | undefined;
  available: AssetRow[];
  onClose: () => void;
  onDone: () => void;
}) {
  const reassign = !!asset;
  const assigneesApi = useApi((signal) => listAssignees(signal), []);
  const roster = assigneesApi.data?.assignees ?? [];
  const [assetId, setAssetId] = useState(asset?.id ?? "");
  const [holderId, setHolderId] = useState("");
  const [note, setNote] = useState("");
  const { run, pending, error } = useMutation((args: { id: string; user: string; note: string }) =>
    assignAsset(args.id, args.user, { note: args.note || null }),
  );

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!assetId || !holderId) return;
    const res = await run({ id: assetId, user: holderId, note });
    if (res) onDone();
  }

  return (
    <Modal
      title={reassign ? `Reassign ${asset!.name}` : "Check out asset"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button className="btn primary" type="submit" form="assign-form" disabled={pending || !assetId || !holderId}>
            {pending ? <Spinner label="Saving…" /> : reassign ? "Reassign" : "Check out"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="assign-form" onSubmit={onSubmit}>
        {reassign ? (
          <p className="hint" style={{ marginTop: 0 }}>
            Currently held by <strong>{asset!.holder_name}</strong>. Pick the new holder below.
          </p>
        ) : (
          <label className="field">
            <span className="label">Asset</span>
            <select value={assetId} onChange={(e) => setAssetId(e.target.value)} required>
              <option value="">Select an available asset…</option>
              {available.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                  {a.asset_tag ? ` (${a.asset_tag})` : ""}
                </option>
              ))}
            </select>
            {available.length === 0 ? <span className="hint">No in-stock assets available to check out.</span> : null}
          </label>
        )}
        <label className="field">
          <span className="label">{reassign ? "New holder" : "Holder"}</span>
          <select value={holderId} onChange={(e) => setHolderId(e.target.value)} disabled={assigneesApi.loading} required>
            <option value="">Select a person…</option>
            {roster.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="label">Note (optional)</span>
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Desk setup, loaner…" />
        </label>
      </form>
    </Modal>
  );
}

/** Roster-wide custody history as holding PERIODS — one row per "who held what,
 *  from when to when", newest first. An open period (no end) is the current
 *  holder. Re-mounts via its `key` after each custody change. */
function AssignmentHistory() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApi((signal) => listAssignmentHistory(200, signal), []);
  const [q, setQ] = useState("");
  const rows = data?.history ?? [];
  // Filter history by holder / asset / tag / who-performed-it / note.
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((h) =>
      [h.user_name, h.asset_name, h.asset_tag, h.by_user_name, h.note].some((s) =>
        (s ?? "").toLowerCase().includes(term),
      ),
    );
  }, [rows, q]);
  const { rows: sortedRows, sort, toggle } = useTableSort(filtered, HISTORY_SORT);
  const pg = usePagination(sortedRows);
  useEffect(() => pg.setPage(0), [q]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="table-filter">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by holder, asset, tag, who, or note…"
          aria-label="Filter custody history"
        />
      </div>
      <div className="card">
      <div className="card-head">
        <h3>History</h3>
        <span className="faint">{filtered.length}</span>
      </div>
      <div className="card-body tight">
        {loading ? (
          <Loading label="Loading history…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : rows.length === 0 ? (
          <div style={{ padding: 18 }}>
            <EmptyState>No custody history yet — check-outs and check-ins will show up here.</EmptyState>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 18 }}>
            <EmptyState>No history matches “{q}”.</EmptyState>
          </div>
        ) : (
          <>
          <table className="data">
            <thead>
              <tr>
                <SortTh field="holder" sort={sort} onSort={toggle}>Holder</SortTh>
                <SortTh field="asset" sort={sort} onSort={toggle}>Asset</SortTh>
                <SortTh field="from" sort={sort} onSort={toggle}>From</SortTh>
                <SortTh field="to" sort={sort} onSort={toggle}>To</SortTh>
                <SortTh field="by" sort={sort} onSort={toggle}>By</SortTh>
                <SortTh field="note" sort={sort} onSort={toggle}>Note</SortTh>
              </tr>
            </thead>
            <tbody>
              {pg.pageRows.map((h) => (
                <tr key={h.id}>
                  <td>
                    <strong>{h.user_name || "—"}</strong>
                  </td>
                  <td>
                    <button className="task-title-link" onClick={() => navigate(`/assets/${h.asset_id}`)}>
                      {h.asset_name}
                    </button>
                    {h.asset_tag ? <span className="muted"> · {h.asset_tag}</span> : null}
                  </td>
                  <td className="muted" style={{ whiteSpace: "nowrap" }}>{formatDateShort(h.start_at)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {h.end_at ? (
                      <span className="muted">{formatDateShort(h.end_at)}</span>
                    ) : (
                      <span className="pill ok">Current</span>
                    )}
                  </td>
                  <td className="muted">{h.by_user_name || "—"}</td>
                  <td className="muted">{h.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination pg={pg} />
          </>
        )}
      </div>
      </div>
    </>
  );
}
